import React, { useState, useMemo, useRef, useEffect, Suspense } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Drumstick, Gamepad2, Hand, Moon, Pill, Sparkles, HeartPulse,
  Zap, Smile, Coins, Lock, Move3d, Star, Volume2, VolumeX, Camera,
} from "lucide-react";
import MochiReal from "../components/mochi/MochiReal";
import { Confetti, NightSky, isNightTime } from "../components/mochi/StageEffects";
import MochiPhotoModal from "../components/mochi/MochiPhotoModal";
import { SFX, soundEnabled, setSoundEnabled } from "../utils/mochiSound";
import { useApp, completedCategories } from "../context/AppState";
import { baseMood, MOOD_LABELS, randomSpeech, ACCESSORIES, xpForLevel, petName } from "../data/mochi";
import { NameChat, CostumeCloset } from "../components/mochi/MochiPanels";
import { autoSeason, SEASONS, SEASON_ORDER } from "../data/season";
import { HOLIDAY_ACC } from "../data/holidays";

const METERS = [
  { key: "kenyang", label: "Kenyang", icon: Drumstick, color: "#d99b5c" },
  { key: "bahagia", label: "Bahagia", icon: Smile, color: "#e07a95" },
  { key: "energi", label: "Energi", icon: Zap, color: "#e0b24b" },
  { key: "kesehatan", label: "Kesehatan", icon: HeartPulse, color: "#68b07c" },
];

function Meter({ icon: Icon, label, value, color }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[13px] text-[#6e5f57]">
        <Icon className="h-4 w-4" style={{ color }} />
        {label}
        <strong className="ml-auto text-[14px] font-bold text-[#4b3f38]">{Math.round(value)}</strong>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#efe7df]">
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

export default function MochiPage() {
  const { state, FEED_COST, MED_COST, feed, playWith, cuddle, sleep, medicate, revive, equipAccessory, equipCostume, renameMochi, anniversaryToday } = useApp();
  const [transient, setTransient] = useState(null);
  const [sound, setSound] = useState(soundEnabled());
  const [party, setParty] = useState(false);
  const [shot, setShot] = useState(null);
  const captureRef = useRef(null);
  const lastTouch = useRef(0);
  const [speech, setSpeech] = useState("Dekat kalian rasanya nyaman.");
  const [pulse, setPulse] = useState(0);
  const [seasonId, setSeasonId] = useState(autoSeason());
  const timerRef = useRef(null);
  const prevLevel = useRef(null);

  const theme = SEASONS[seasonId] || SEASONS.hangat;
  const cycleSeason = () => {
    const i = SEASON_ORDER.indexOf(seasonId);
    setSeasonId(SEASON_ORDER[(i + 1) % SEASON_ORDER.length]);
  };

  useEffect(() => {
    const lvl = state?.mochi?.level;
    if (lvl == null) return;
    if (prevLevel.current != null && lvl > prevLevel.current) {
      toast.success(`${petName(state?.mochi)} naik ke Level ${lvl}! +${lvl * 50} poin 🎉`);
      setMoodTransient("levelup", 3800); setPulse((p) => p + 1); SFX.levelup();
      setParty(true); setTimeout(() => setParty(false), 4200);
    }
    prevLevel.current = lvl;
  }, [state?.mochi?.level]); // eslint-disable-line

  const m = state?.mochi;
  const name = petName(m);
  const dead = m && !m.alive;
  const derived = m ? baseMood(m) : "happy";
  const mood = transient || (anniversaryToday && !dead ? "joy" : derived);

  const setMoodTransient = (mm, ms = 2800) => {
    setTransient(mm);
    setSpeech(randomSpeech(mm));
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTransient(null), ms);
  };

  const react = () => {
    setSpeech(randomSpeech(mood)); setPulse((p) => p + 1);
    if (!dead && Date.now() - lastTouch.current > 400) SFX.tap();
  };
  // sentuhan pada bagian tubuh Mochi (dari raycast 3D): pipi/kepala -> meong kecil + reaksi manja
  const onTouch = (part) => {
    if (dead) return;
    lastTouch.current = Date.now();
    if (part === "cheek" || part === "head") { SFX.cheek(); setTransient("cheek"); setSpeech(randomSpeech("cheek")); clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setTransient(null), 1300); }
  };
  const night = isNightTime() || mood === "sleep";
  // dipanggil namanya di kolom obrolan -> menoleh (pulse), meong, dan menjawab
  const onCall = (reply) => {
    if (dead) return;
    setTransient("joy"); setSpeech(reply); setPulse((p) => p + 1); SFX.call();
    clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setTransient(null), 2600);
  };
  const onChat = (reply) => { if (!dead) setSpeech(reply); };
  const takePhoto = () => {
    const fn = captureRef.current;
    if (!fn) return toast.error("Mochi belum siap difoto, tunggu sebentar.");
    try { setShot(fn()); } catch { toast.error("Gagal memotret. Coba lagi."); }
  };
  const toggleSound = () => { const next = !sound; setSound(next); setSoundEnabled(next); if (next) SFX.tap(); };

  const doFeed = () => {
    if (state.points < FEED_COST) return toast.error("Poin tidak cukup untuk memberi makan");
    feed(); setMoodTransient("eating", 4200); setPulse((p) => p + 1); SFX.feed();
  };
  const doPlay = () => {
    if (m.energi < 10) return toast.error("Mochi terlalu lelah untuk bermain");
    playWith(); setMoodTransient("play"); setPulse((p) => p + 1); SFX.play();
  };
  const doCuddle = () => { cuddle(); setMoodTransient("cuddle"); setPulse((p) => p + 1); SFX.cuddle(); };
  const doSleep = () => { sleep(); setMoodTransient("sleep", 3200); SFX.sleep(); };
  const doMed = () => {
    if (state.points < MED_COST) return toast.error("Poin tidak cukup untuk membeli obat");
    medicate(); setMoodTransient("eating", 2200); SFX.med();
  };
  const doRevive = () => { revive(); setMoodTransient("revive", 3600); setPulse((p) => p + 1); SFX.revive(); toast.success("Mochi bangkit kembali! ✨"); };

  const unlocked = m?.unlocked || [];

  const actions = useMemo(() => ([
    { id: "feed", label: "Beri makan", cost: `${FEED_COST} poin`, icon: Drumstick, onClick: doFeed, primary: true, disabled: dead },
    { id: "play", label: "Bermain", cost: "Gratis", icon: Gamepad2, onClick: doPlay, disabled: dead },
    { id: "cuddle", label: "Elus", cost: "Gratis", icon: Hand, onClick: doCuddle, disabled: dead },
    { id: "sleep", label: "Tidur", cost: "Gratis", icon: Moon, onClick: doSleep, disabled: dead },
    { id: "med", label: "Obat", cost: `${MED_COST} poin`, icon: Pill, onClick: doMed, disabled: dead },
  ]), [state, dead, m]);

  if (!m) return null;

  return (
    <div className="kk-reveal">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#ff5a5f]">
            <Sparkles className="h-3.5 w-3.5" /> Peliharaan kalian
          </div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="pet-title">{name} kita</h1>
          <p className="mt-1 text-[14px] text-[#8a8278]">Peliharaan {state.coupleName} · rawat bersama tiap hari.</p>
        </div>
        <Link to="/mochi-demo" data-testid="open-expression-panel"
          className="hidden items-center gap-2 rounded-full bg-[#ffebeb] px-4 py-2 text-[12px] font-bold text-[#ff5a5f] transition-colors hover:bg-[#ffd9d9] sm:flex">
          <Sparkles className="h-3.5 w-3.5" /> Panel ekspresi
        </Link>
      </div>

      {anniversaryToday && !dead && (
        <div className="mb-5 flex items-center gap-3 rounded-3xl kk-coral-gradient p-4 text-white kk-pop" data-testid="mochi-anniversary-banner">
          <Sparkles className="h-5 w-5 flex-none" />
          <p className="text-[14px] font-bold">Hari jadian kalian! Mochi lagi bahagia banget & kalian dapat bonus poin 🎉</p>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1.25fr_1fr]">
        {/* 3D stage */}        <div className="relative overflow-hidden rounded-3xl border border-[#e6d9cc] transition-colors duration-700" data-testid="mochi-stage" data-night={night ? "1" : "0"}
          style={{ background: dead ? "radial-gradient(ellipse at 40% 30%,#eceae9,#d6d1cf)" : night ? "radial-gradient(ellipse at 40% 25%,#33406b 0%,#1c2340 55%,#12172b 100%)" : theme.bg }}>
          {night && !dead && <NightSky />}
          {party && <Confetti />}
          <div className="absolute left-4 right-4 top-4 z-10 flex items-center justify-between">
            <div className="rounded-full bg-white/70 px-3 py-1.5 text-[12px] font-bold text-[#8d785b] backdrop-blur">
              Level {m.level}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={cycleSeason} data-testid="season-toggle"
                className="hidden items-center gap-1 rounded-full bg-white/75 px-3 py-1.5 text-[12px] font-bold backdrop-blur transition-transform active:scale-95 sm:flex"
                style={{ color: theme.badge }} title="Ganti tema musim">
                <span>{theme.emoji}</span> {theme.label}
              </button>
              <div className="rounded-full bg-white/70 px-3 py-1.5 text-[12px] font-semibold text-[#758465] backdrop-blur" data-testid="mood-label">
                {MOOD_LABELS[mood]}
              </div>
              <button onClick={toggleSound} data-testid="sound-toggle" aria-pressed={sound}
                title={sound ? "Matikan suara Mochi" : "Nyalakan suara Mochi"}
                className={`grid h-8 w-8 place-items-center rounded-full backdrop-blur transition-colors ${sound ? "bg-white/75 text-[#ff5a5f]" : "bg-white/50 text-[#a89f98]"}`}>
                {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="pointer-events-none absolute left-1/2 top-16 z-10 max-w-[80%] -translate-x-1/2 rounded-2xl bg-white/90 px-4 py-2 text-center text-[13px] font-medium text-[#7a6a5f] shadow-sm backdrop-blur">
            {dead ? `${name} pingsan… bangkitkan yuk.` : speech}
          </div>

          <div className="h-[440px] w-full sm:h-[520px]">
            <Suspense fallback={<div className="grid h-full place-items-center text-[#a89f98]">Memuat Mochi…</div>}>
              <MochiReal mood={mood} accessory={m.equipped} costume={m.outfit} actionPulse={pulse} onReact={react} onTouch={onTouch} night={night && !dead} captureRef={captureRef} theme={dead || mood === "sleep" ? null : theme} />
            </Suspense>
          </div>

          <div className="pointer-events-none absolute bottom-4 left-4 z-10 flex max-w-[55%] items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-[11px] font-medium text-[#8c7e6b] backdrop-blur sm:max-w-none">
            <Move3d className="h-3.5 w-3.5 flex-none" /> <span className="truncate">Geser maks 15° · sentuh untuk bereaksi</span>
          </div>
          <div className="pointer-events-none absolute bottom-4 right-4 z-10 hidden max-w-[45%] items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-[11px] font-medium text-[#8c7e6b] backdrop-blur sm:flex">
            {theme.emoji} {theme.note}
          </div>
          <button onClick={takePhoto} data-testid="photo-button" title="Foto bareng Mochi"
            className="absolute bottom-16 right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/85 text-[#ff5a5f] shadow-md backdrop-blur transition-transform active:scale-95 sm:bottom-16">
            <Camera className="h-5 w-5" />
          </button>
          <button onClick={cycleSeason} data-testid="season-toggle-mobile"
            className="absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-full bg-white/75 px-3 py-1.5 text-[11px] font-bold backdrop-blur transition-transform active:scale-95 sm:hidden"
            style={{ color: theme.badge }} title="Ganti tema musim">
            <span>{theme.emoji}</span> {theme.label}
          </button>
        </div>

        {/* controls */}
        <div className="space-y-4">
          {/* XP */}
          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <div className="flex items-center justify-between text-[13px]">
              <span className="font-bold text-[#4b3f38]">Level {m.level}</span>
              <span className="text-[#8a8278]">{m.xp} / {xpForLevel(m.level)} XP</span>
            </div>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#efe7df]">
              <div className="h-full rounded-full kk-coral-gradient transition-[width] duration-500" style={{ width: `${(m.xp / xpForLevel(m.level)) * 100}%` }} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {METERS.map((mt) => <Meter key={mt.key} icon={mt.icon} label={mt.label} value={m[mt.key]} color={mt.color} />)}
            </div>
          </div>

          {/* care actions */}
          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[16px] font-bold">Rawat {name}</h2>
              <span className="flex items-center gap-1 text-[12px] font-semibold text-[#8a6b3d]">
                <Coins className="h-3.5 w-3.5 text-[#e0a94b]" />{Number(state.points).toLocaleString("id-ID")} poin
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5">
              {actions.map((a) => (
                <button
                  key={a.id}
                  onClick={a.onClick}
                  disabled={a.disabled}
                  data-testid={`care-${a.id}`}
                  className={`flex flex-col items-center gap-1 rounded-2xl border p-3 transition-all active:scale-95 disabled:opacity-40 ${
                    a.primary ? "border-transparent kk-coral-gradient text-white" : "border-[#eee5da] bg-[#f9f5f0] text-[#6e594c]"
                  }`}
                >
                  <a.icon className="h-5 w-5" />
                  <span className="text-[12px] font-bold">{a.label}</span>
                  <span className={`text-[10px] ${a.primary ? "text-white/80" : "text-[#a19183]"}`}>{a.cost}</span>
                </button>
              ))}
            </div>

            {dead && (
              <div className="mt-4 rounded-2xl border border-[#e5d1a8] bg-[#fbf3e0] p-4">
                <p className="text-[13px] text-[#9a7f5c]">Mochi butuh dibangkitkan. Di demo ini gratis dan tidak menghapus level, aksesori, poin, atau riwayat.</p>
                <button
                  onClick={doRevive}
                  data-testid="care-revive"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full kk-coral-gradient py-3 text-[14px] font-bold text-white"
                >
                  <Sparkles className="h-4 w-4" /> Bangkitkan Mochi (gratis)
                </button>
              </div>
            )}
          </div>

          <NameChat name={name} onRename={renameMochi} onCall={onCall} onChat={onChat} />
          <CostumeCloset level={m.level} outfit={m.outfit} onEquip={equipCostume} cats={completedCategories(state)} />

          {/* accessories */}
          <div className="rounded-3xl border border-[#efeae4] bg-white p-5">
            <h2 className="mb-3 text-[16px] font-bold">Lemari aksesori</h2>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
              {ACCESSORIES.map((a) => {
                const isUnlocked = unlocked.includes(a.id);
                const equipped = m.equipped === a.id;
                return (
                  <button
                    key={a.id}
                    onClick={() => isUnlocked ? equipAccessory(a.id) : toast(`Buka di level ${a.reqLevel}`)}
                    data-testid={`accessory-${a.id}`}
                    className={`relative rounded-2xl border p-2 text-center transition-colors ${
                      equipped ? "border-[#ff5a5f] bg-[#ffebeb]" : "border-[#efeae4] bg-[#fcfaf7]"
                    } ${!isUnlocked ? "opacity-55" : ""}`}
                  >
                    <div className="mx-auto mb-1 grid h-8 place-items-center text-[20px]">{ACC_EMOJI[a.id]}</div>
                    <div className="text-[10px] font-semibold leading-tight text-[#6e594c]">{a.label}</div>
                    {!isUnlocked && <Lock className="absolute right-1.5 top-1.5 h-3 w-3 text-[#a99] " />}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-[#a69995]">Naik level dari merawat Mochi & menyelesaikan kencan untuk membuka aksesori baru.</p>

            {(m.specialUnlocked || []).length > 0 && (
              <div className="mt-4 border-t border-dashed border-[#f0d3da] pt-4" data-testid="special-closet">
                <h3 className="mb-2 flex items-center gap-1.5 text-[13px] font-bold text-[#c9566b]">
                  <Sparkles className="h-4 w-4" /> Aksesori spesial hari besar
                </h3>
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                  {(m.specialUnlocked || []).map((id) => {
                    const meta = HOLIDAY_ACC[id];
                    if (!meta) return null;
                    const equipped = m.equipped === id;
                    return (
                      <button
                        key={id}
                        onClick={() => equipAccessory(id)}
                        data-testid={`special-accessory-${id}`}
                        className={`relative rounded-2xl border p-2 text-center transition-colors ${
                          equipped ? "border-[#ff5a5f] bg-[#ffebeb]" : "border-[#f3dbe1] bg-[#fff7f9]"
                        }`}
                      >
                        <div className="mx-auto mb-1 grid h-8 place-items-center text-[20px]">{meta.emoji}</div>
                        <div className="text-[10px] font-semibold leading-tight text-[#6e594c]">{meta.label}</div>
                        <Star className="absolute right-1.5 top-1.5 h-3 w-3 fill-[#f0b429] text-[#f0b429]" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <MochiPhotoModal open={!!shot} onClose={() => setShot(null)} shot={shot} coupleName={state.coupleName} level={m.level}
        moodLabel={MOOD_LABELS[mood]} accessoryLabel={accLabel(m.equipped)}
        bg={night ? "radial-gradient(ellipse at 40% 25%,#33406b 0%,#1c2340 55%,#12172b 100%)" : theme.bg} />
    </div>
  );
}

const ACC_EMOJI = { bow: "🎀", flower: "🌸", glasses: "🕶️", hat: "👒", crown: "👑", scarf: "🧣", balloon: "🎈" };
const accLabel = (id) => ACCESSORIES.find((a) => a.id === id)?.label || HOLIDAY_ACC[id]?.label || "";
